import React, { useMemo, useState, useEffect } from 'react';
import ThinHeader from '../components/ThinHeader';
import { getStatsOverview, getStatsBattle, getPeriodLeaderboard, getPlayerPointsBreakdown } from '../api/gameApi';
import { getAdminStatsAudit, reconcileAdminUserStats } from '../api/adminApi';
import { useAuth } from '../context/AuthContext';

const defaultPlayerSort = { key: 'totalPoints', direction: 'desc' };
const defaultGameSort = { key: 'createdAt', direction: 'desc' };

const compareValues = (a, b, direction = 'asc') => {
  const aa = a ?? '';
  const bb = b ?? '';
  if (typeof aa === 'number' && typeof bb === 'number') {
    return direction === 'asc' ? aa - bb : bb - aa;
  }
  const av = `${aa}`.toLowerCase();
  const bv = `${bb}`.toLowerCase();
  if (av < bv) return direction === 'asc' ? -1 : 1;
  if (av > bv) return direction === 'asc' ? 1 : -1;
  return 0;
};

const percent = (value, total) => {
  const v = Number(value || 0);
  const t = Number(total || 0);
  if (t <= 0) return 0;
  return Math.round((v / t) * 100);
};

const separationPercentForA = (aPoints, bPoints) => {
  const a = Number(aPoints || 0);
  const b = Number(bPoints || 0);
  const total = a + b;
  if (total <= 0) return 50;
  return Math.max(0, Math.min(100, Math.round((a / total) * 100)));
};

const formatRelativeTime = (dateInput) => {
  if (!dateInput) return 'unknown';
  const ts = new Date(dateInput).getTime();
  if (!Number.isFinite(ts)) return 'unknown';

  const deltaSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  return `${Math.floor(deltaSec / 86400)}d ago`;
};

const activityBadge = (entry) => {
  if (entry?.onlineNow) {
    return { text: 'Online now', className: 'online', title: 'Player is online in Necronomicon right now' };
  }
  if (entry?.lastActiveAt) {
    const exact = new Date(entry.lastActiveAt).toLocaleString();
    return {
      text: `Last active ${formatRelativeTime(entry.lastActiveAt)}`,
      className: 'offline',
      title: `Last active ${exact}`,
    };
  }
  return { text: 'Last active unknown', className: 'offline', title: 'No recent activity recorded' };
};

const renderActivityBadge = (entry) => {
  const badge = activityBadge(entry);
  return <span className={`leader-activity-badge ${badge.className}`} title={badge.title}>{badge.text}</span>;
};

const StatsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);

  const [playerQuery, setPlayerQuery] = useState('');
  const [gameQuery, setGameQuery] = useState('');
  const [playerSort, setPlayerSort] = useState(defaultPlayerSort);
  const [gameSort, setGameSort] = useState(defaultGameSort);
  const [userA, setUserA] = useState('');
  const [userB, setUserB] = useState('');
  const [battleLoading, setBattleLoading] = useState(false);
  const [battleError, setBattleError] = useState('');
  const [battleResult, setBattleResult] = useState(null);
  const [tournamentPeriod, setTournamentPeriod] = useState('monthly');
  const [tournamentBoard, setTournamentBoard] = useState([]);
  const [tournamentMeta, setTournamentMeta] = useState(null);
  const [tournamentLoading, setTournamentLoading] = useState(false);
  const [tournamentHistory, setTournamentHistory] = useState({ weekly: [], monthly: [] });
  const [historyPeriod, setHistoryPeriod] = useState('monthly');
  const [showFullTopPlayers, setShowFullTopPlayers] = useState(false);
  const [allPlayersSectionOpen, setAllPlayersSectionOpen] = useState(false);
  const [allGamesSectionOpen, setAllGamesSectionOpen] = useState(false);
  const [showAuditColumns, setShowAuditColumns] = useState(false);
  const [statsAuditLoading, setStatsAuditLoading] = useState(false);
  const [statsAuditError, setStatsAuditError] = useState('');
  const [statsAuditSummary, setStatsAuditSummary] = useState(null);
  const [statsAuditByUserId, setStatsAuditByUserId] = useState({});
  const [reconcilingUserId, setReconcilingUserId] = useState(null);
  const [statsAuditNotice, setStatsAuditNotice] = useState('');
  const [breakdownLoadingUserId, setBreakdownLoadingUserId] = useState(null);
  const [breakdownShowNonZeroOnly, setBreakdownShowNonZeroOnly] = useState(false);
  const [breakdownModal, setBreakdownModal] = useState({
    open: false,
    playerName: '',
    summary: null,
    rounds: [],
    error: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [{ data }, auditRes] = await Promise.all([
          getStatsOverview(),
          user?.role === 'admin' ? getAdminStatsAudit() : Promise.resolve(null),
        ]);
        setPlayers(data?.players || []);
        setGames(data?.games || []);
        setTournamentHistory(data?.tournamentHistory || { weekly: [], monthly: [] });

        if (auditRes?.data) {
          const users = Array.isArray(auditRes.data.users) ? auditRes.data.users : [];
          const nextAuditByUserId = users.reduce((acc, entry) => {
            const key = `${entry?.userId || ''}`;
            if (!key) return acc;
            acc[key] = entry;
            return acc;
          }, {});

          setStatsAuditSummary(auditRes.data.summary || null);
          setStatsAuditByUserId(nextAuditByUserId);
          setStatsAuditError('');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Could not load stats');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.role]);

  const runStatsAudit = async () => {
    if (user?.role !== 'admin') return;
    try {
      setStatsAuditLoading(true);
      setStatsAuditError('');
      setStatsAuditNotice('');
      const { data } = await getAdminStatsAudit();
      const users = Array.isArray(data?.users) ? data.users : [];
      const nextAuditByUserId = users.reduce((acc, entry) => {
        const key = `${entry?.userId || ''}`;
        if (!key) return acc;
        acc[key] = entry;
        return acc;
      }, {});

      setStatsAuditSummary(data?.summary || null);
      setStatsAuditByUserId(nextAuditByUserId);
    } catch (err) {
      setStatsAuditError(err.response?.data?.error || 'Could not run stats audit');
    } finally {
      setStatsAuditLoading(false);
    }
  };

  const refreshOverview = async () => {
    const { data } = await getStatsOverview();
    setPlayers(data?.players || []);
    setGames(data?.games || []);
    setTournamentHistory(data?.tournamentHistory || { weekly: [], monthly: [] });
  };

  const handleReconcileUser = async (player) => {
    if (!player?.userId) return;
    try {
      setReconcilingUserId(player.userId);
      setStatsAuditError('');
      setStatsAuditNotice('');
      const { data } = await reconcileAdminUserStats(player.userId);
      await Promise.all([refreshOverview(), runStatsAudit()]);
      const deltaPoints = data?.delta?.totalPoints || 0;
      const deltaGames = data?.delta?.gamesPlayed || 0;
      const deltaWins = data?.delta?.gamesWon || 0;
      setStatsAuditNotice(`${player.displayName}: reconciled (points ${deltaPoints >= 0 ? '+' : ''}${deltaPoints}, games ${deltaGames >= 0 ? '+' : ''}${deltaGames}, wins ${deltaWins >= 0 ? '+' : ''}${deltaWins}).`);
    } catch (err) {
      setStatsAuditError(err.response?.data?.error || 'Could not reconcile user stats');
    } finally {
      setReconcilingUserId(null);
    }
  };

  const openPlayerBreakdown = async (player) => {
    try {
      setBreakdownLoadingUserId(player.userId);
      setBreakdownShowNonZeroOnly(false);
      const { data } = await getPlayerPointsBreakdown(player.userId);
      setBreakdownModal({
        open: true,
        playerName: data?.player?.displayName || player.displayName || 'Player',
        summary: data?.summary || null,
        rounds: data?.rounds || [],
        error: '',
      });
    } catch (err) {
      setBreakdownModal({
        open: true,
        playerName: player.displayName || 'Player',
        summary: null,
        rounds: [],
        error: err.response?.data?.error || 'Could not load player points breakdown',
      });
    } finally {
      setBreakdownLoadingUserId(null);
    }
  };

  useEffect(() => {
    const loadTournamentBoard = async () => {
      try {
        setTournamentLoading(true);
        const { data } = await getPeriodLeaderboard({
          period: tournamentPeriod,
          limit: 25,
          tournamentOnly: true,
        });
        setTournamentBoard(data?.leaderboard || []);
        setTournamentMeta({
          periodLabel: data?.periodLabel || '',
          tournamentName: data?.tournamentName || 'Tournament',
          participantsTotal: Number(data?.participantsTotal || 0),
        });
      } catch {
        setTournamentBoard([]);
        setTournamentMeta(null);
      } finally {
        setTournamentLoading(false);
      }
    };

    loadTournamentBoard();
  }, [tournamentPeriod]);

  const top20Players = useMemo(() => players.slice(0, 20), [players]);
  const visibleTopPlayers = useMemo(
    () => (showFullTopPlayers ? players : top20Players),
    [players, showFullTopPlayers, top20Players]
  );

  const filteredPlayers = useMemo(() => {
    const q = playerQuery.trim().toLowerCase();
    const base = q
      ? players.filter((p) =>
        `${p.displayName} ${p.username} ${p.role}`.toLowerCase().includes(q)
      )
      : [...players];

    return base.sort((a, b) => compareValues(a[playerSort.key], b[playerSort.key], playerSort.direction));
  }, [players, playerQuery, playerSort]);

  const filteredGames = useMemo(() => {
    const q = gameQuery.trim().toLowerCase();
    const base = q
      ? games.filter((g) =>
        `${g.roomCode} ${g.gameName} ${g.status} ${g.tournamentLabel || ''} ${g.tournamentRecurrence || ''}`.toLowerCase().includes(q)
      )
      : [...games];

    return base.sort((a, b) => compareValues(a[gameSort.key], b[gameSort.key], gameSort.direction));
  }, [games, gameQuery, gameSort]);

  const terminatorUserIds = useMemo(() => {
    if (!players.length) return new Set();
    const maxBotsDefeated = Math.max(...players.map((p) => Number(p.npcOpponentsDefeated || 0)));
    if (maxBotsDefeated <= 0) return new Set();
    return new Set(
      players
        .filter((p) => Number(p.npcOpponentsDefeated || 0) === maxBotsDefeated)
        .map((p) => String(p.userId))
    );
  }, [players]);

  const filteredPlayersWithAudit = useMemo(() => {
    return filteredPlayers.map((player) => {
      const audit = statsAuditByUserId[`${player.userId}`] || null;
      return {
        ...player,
        auditStoredPoints: audit?.stored?.totalPoints ?? null,
        auditExpectedPoints: audit?.expected?.totalPoints ?? null,
        auditDeltaPoints: audit?.delta?.totalPoints ?? null,
        auditStoredGames: audit?.stored?.gamesPlayed ?? null,
        auditExpectedGames: audit?.expected?.gamesPlayed ?? null,
        auditDeltaGames: audit?.delta?.gamesPlayed ?? null,
        auditStoredWins: audit?.stored?.gamesWon ?? null,
        auditExpectedWins: audit?.expected?.gamesWon ?? null,
        auditDeltaWins: audit?.delta?.gamesWon ?? null,
        auditStoredRoundsWon: audit?.stored?.roundsWon ?? null,
        auditExpectedRoundsWon: audit?.expected?.roundsWon ?? null,
        auditDeltaRoundsWon: audit?.delta?.roundsWon ?? null,
        auditStoredCourtesy: audit?.stored?.courtesyShuffles ?? null,
        auditExpectedCourtesy: audit?.expected?.courtesyShuffles ?? null,
        auditDeltaCourtesy: audit?.delta?.courtesyShuffles ?? null,
        auditHasMismatch: !!audit?.hasMismatch,
      };
    });
  }, [filteredPlayers, statsAuditByUserId]);

  const displayedBreakdownRounds = useMemo(() => {
    if (!breakdownShowNonZeroOnly) return breakdownModal.rounds || [];
    return (breakdownModal.rounds || []).filter((round) => Number(round.delta || 0) !== 0);
  }, [breakdownModal.rounds, breakdownShowNonZeroOnly]);

  const togglePlayerSort = (key) => {
    setPlayerSort((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'desc' }
    ));
  };

  const toggleGameSort = (key) => {
    setGameSort((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'desc' }
    ));
  };

  const runBattle = async () => {
    if (!userA) {
      setBattleError('Select User A first.');
      return;
    }

    try {
      setBattleLoading(true);
      setBattleError('');
      const { data } = await getStatsBattle(userA, userB || undefined);
      setBattleResult(data);
    } catch (err) {
      setBattleError(err.response?.data?.error || 'Could not run stats battle');
    } finally {
      setBattleLoading(false);
    }
  };

  return (
    <div className="stats-page">
      <ThinHeader
        homeTo="/"
        userLabel={user?.displayName || user?.username || user?.email || 'Player'}
        showAdminLink={user?.role === 'admin'}
      />

      <div className="stats-title-row">
        <h1>Global Stats</h1>
      </div>

      {error && <p className="error">{error}</p>}
      {loading ? <p>Loading stats...</p> : null}

      <section className="stats-card">
        <h2>Stats Battle</h2>
        <p className="admin-note">Compare User A vs User B, or leave User B empty for User A vs World.</p>
        <div className="stats-battle-controls">
          <label>
            User A
            <select value={userA} onChange={(e) => setUserA(e.target.value)}>
              <option value="">Select player...</option>
              {players.map((p) => (
                <option key={p.userId} value={p.userId}>{p.displayName}</option>
              ))}
            </select>
          </label>
          <label>
            User B (optional)
            <select value={userB} onChange={(e) => setUserB(e.target.value)}>
              <option value="">World comparison only</option>
              {players.filter((p) => p.userId !== userA).map((p) => (
                <option key={p.userId} value={p.userId}>{p.displayName}</option>
              ))}
            </select>
          </label>
          <button type="button" className="btn-play" onClick={runBattle} disabled={battleLoading || !userA}>
            {battleLoading ? 'Computing...' : 'Run Battle'}
          </button>
        </div>

        {battleError && <p className="error">{battleError}</p>}

        {battleResult && (
          <div className="stats-battle-results">
            {(() => {
              const a = battleResult.userA;
              const b = battleResult.userB;
              const worldAvg = battleResult.world?.averages || {};
              const worldCount = battleResult.world?.playerCount || 0;
              const h2h = battleResult.headToHead;

              const rightName = b?.displayName || 'World Average';
              const rightRank = b?.rank ? `Rank #${b.rank}` : `Across ${worldCount} players`;
              const rightPoints = b?.totalPoints ?? worldAvg.totalPoints ?? 0;
              const rightWins = b?.gamesWon ?? worldAvg.gamesWon ?? 0;

              const pointsTotal = (a?.totalPoints || 0) + Number(rightPoints || 0);
              const winsTotal = (a?.gamesWon || 0) + Number(rightWins || 0);

              const aPointPct = percent(a?.totalPoints, pointsTotal);
              const rightPointPct = percent(rightPoints, pointsTotal);
              const aWinPct = percent(a?.gamesWon, winsTotal);
              const rightWinPct = percent(rightWins, winsTotal);

              const roundsTotal = h2h ? ((h2h.aBeatB || 0) + (h2h.bBeatA || 0) + (h2h.ties || 0)) : 0;
              const aBeatPct = percent(h2h?.aBeatB, roundsTotal);
              const bBeatPct = percent(h2h?.bBeatA, roundsTotal);
              const tiesPct = percent(h2h?.ties, roundsTotal);

              const koText = h2h && h2h.roundsTogether > 0
                ? (h2h.aBeatB > 0 && h2h.bBeatA === 0
                  ? `${a?.displayName} delivered a T.K.O.`
                  : (h2h.bBeatA > 0 && h2h.aBeatB === 0
                    ? `${b?.displayName} delivered a T.K.O.`
                    : ''))
                : '';

              const globalKoByPoints = pointsTotal > 0
                ? (aPointPct === 100 && rightPointPct === 0
                  ? `${a?.displayName} delivered a T.K.O. by points share.`
                  : (rightPointPct === 100 && aPointPct === 0
                    ? `${rightName} delivered a T.K.O. by points share.`
                    : ''))
                : '';

              const globalKoByWins = winsTotal > 0
                ? (aWinPct === 100 && rightWinPct === 0
                  ? `${a?.displayName} delivered a T.K.O. by wins share.`
                  : (rightWinPct === 100 && aWinPct === 0
                    ? `${rightName} delivered a T.K.O. by wins share.`
                    : ''))
                : '';

              return (
                <>
                  <div className="battle-grid">
                    <div className="battle-side-card stats-pill">
                      <span className="leader-name">{a?.displayName}</span>
                      <span className="leader-metrics">Rank #{a?.rank} • {a?.totalPoints} pts • {a?.gamesWon} wins</span>
                      <span className="leader-metrics">Vs players: {a?.humanOpponentsFaced} faced • {a?.humanOpponentsDefeated} defeated</span>
                      <span className="leader-metrics">Vs NPCs: {a?.npcOpponentsFaced} faced • {a?.npcOpponentsDefeated} defeated</span>
                    </div>

                    <div className="battle-center-stack">
                      <div className="stats-pill">
                        <span className="leader-name">Global Comparison</span>
                        <span className="leader-metrics">Points share: {aPointPct}% vs {rightPointPct}%</span>
                        <div className="battle-bar-row">
                          <span>{a?.displayName}</span>
                          <div className="battle-bar"><div className="battle-bar-fill" style={{ width: `${aPointPct}%` }} /></div>
                          <strong>{aPointPct}%</strong>
                        </div>
                        <div className="battle-bar-row">
                          <span>{rightName}</span>
                          <div className="battle-bar"><div className="battle-bar-fill alt" style={{ width: `${rightPointPct}%` }} /></div>
                          <strong>{rightPointPct}%</strong>
                        </div>
                        <span className="leader-metrics">Wins share: {aWinPct}% vs {rightWinPct}%</span>
                        {globalKoByPoints && <strong className="battle-ko">{globalKoByPoints}</strong>}
                        {globalKoByWins && <strong className="battle-ko">{globalKoByWins}</strong>}
                      </div>

                      <div className="stats-pill">
                        <span className="leader-name">Head To Head</span>
                        {h2h ? (
                          <>
                            <span className="leader-metrics">Games together: {h2h.gamesTogether} • Rounds together: {h2h.roundsTogether}</span>
                            <span className="leader-metrics">
                              Total point separation: {h2h.totalPointSeparation} ({a?.displayName}: {h2h.aTotalPoints} vs {b?.displayName}: {h2h.bTotalPoints})
                            </span>
                            <div className="battle-separation-row">
                              <span>{a?.displayName}</span>
                              <div className="battle-separation-track">
                                <div
                                  className={`battle-separation-marker ${h2h.totalPointWinner === 'A' ? 'a' : (h2h.totalPointWinner === 'B' ? 'b' : 'tie')}`}
                                  style={{ left: `${separationPercentForA(h2h.aTotalPoints, h2h.bTotalPoints)}%` }}
                                />
                              </div>
                              <span>{b?.displayName}</span>
                            </div>
                            <div className="battle-bar-row">
                              <span>{a?.displayName}</span>
                              <div className="battle-bar"><div className="battle-bar-fill" style={{ width: `${aBeatPct}%` }} /></div>
                              <strong>{aBeatPct}%</strong>
                            </div>
                            <div className="battle-bar-row">
                              <span>{b?.displayName}</span>
                              <div className="battle-bar"><div className="battle-bar-fill alt" style={{ width: `${bBeatPct}%` }} /></div>
                              <strong>{bBeatPct}%</strong>
                            </div>
                            <div className="battle-bar-row">
                              <span>Ties</span>
                              <div className="battle-bar"><div className="battle-bar-fill tie" style={{ width: `${tiesPct}%` }} /></div>
                              <strong>{tiesPct}%</strong>
                            </div>
                            {koText && <strong className="battle-ko">{koText}</strong>}
                          </>
                        ) : (
                          <span className="leader-metrics">Select User B to see head-to-head battle data.</span>
                        )}
                      </div>
                    </div>

                    <div className="battle-side-card stats-pill">
                      <span className="leader-name">{rightName}</span>
                      <span className="leader-metrics">{rightRank} • {Number(rightPoints || 0).toFixed(2)} pts • {Number(rightWins || 0).toFixed(2)} wins</span>
                      {b ? (
                        <>
                          <span className="leader-metrics">Vs players: {b.humanOpponentsFaced} faced • {b.humanOpponentsDefeated} defeated</span>
                          <span className="leader-metrics">Vs NPCs: {b.npcOpponentsFaced} faced • {b.npcOpponentsDefeated} defeated</span>
                        </>
                      ) : (
                        <>
                          <span className="leader-metrics">Avg games: {worldAvg.gamesPlayed ?? 0}</span>
                          <span className="leader-metrics">Avg rounds won: {worldAvg.roundsWon ?? 0}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {h2h && (
                    <div className="admin-table-wrap">
                      <table className="admin-table stats-table">
                        <thead>
                          <tr>
                            <th>Room</th>
                            <th>Game</th>
                            <th>Rounds Together</th>
                            <th>P1 ----- P2</th>
                            <th>Last Active</th>
                          </tr>
                        </thead>
                        <tbody>
                          {h2h.sharedGames?.map((g) => (
                            <tr key={g.gameId}>
                              <td>{g.roomCode}</td>
                              <td>{g.gameName}</td>
                              <td>{g.roundsTogether}</td>
                              <td>
                                <div className="battle-separation-row battle-separation-row--table">
                                  <span>P1</span>
                                  <div className="battle-separation-track">
                                    <div
                                      className={`battle-separation-marker ${g.winner === 'A' ? 'a' : (g.winner === 'B' ? 'b' : 'tie')}`}
                                      style={{ left: `${separationPercentForA(g.aPoints, g.bPoints)}%` }}
                                    />
                                  </div>
                                  <span>P2</span>
                                </div>
                                <small className="leader-metrics">
                                  {a?.displayName}: {g.aPoints} vs {b?.displayName}: {g.bPoints} • diff {g.pointDiff}
                                </small>
                              </td>
                              <td>{new Date(g.updatedAt).toLocaleString()}</td>
                            </tr>
                          ))}
                          {(h2h.sharedGames || []).length === 0 && (
                            <tr>
                              <td colSpan={5}>No shared games yet.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </section>

      <section className="stats-card">
        <h2>Recurring Tournament Leaderboard</h2>
        <div className="leaderboard-actions-row" style={{ marginBottom: '0.55rem' }}>
          <button type="button" className="thin-header-btn" onClick={() => setTournamentPeriod('monthly')}>
            Monthly
          </button>
          <button type="button" className="thin-header-btn" onClick={() => setTournamentPeriod('weekly')}>
            Weekly
          </button>
        </div>
        {tournamentMeta && (
          <p className="admin-note" style={{ marginBottom: '0.6rem' }}>
            {tournamentMeta.periodLabel || (tournamentPeriod === 'monthly' ? 'Monthly period' : 'Weekly period')}
            {' '}• {tournamentMeta.tournamentName || 'Tournament'}
            {' '}• {tournamentMeta.participantsTotal || 0} participants
          </p>
        )}
        {tournamentLoading ? (
          <p className="header-leaderboard-empty">Loading tournament leaderboard...</p>
        ) : tournamentBoard.length > 0 ? (
          <div className="stats-grid-top10">
            {tournamentBoard.map((entry) => (
              <div key={`tour-${entry.userId}`} className="stats-pill">
                {renderActivityBadge(entry)}
                <span className="leader-rank">#{entry.rank}</span>
                <span className="leader-name">{entry.displayName}</span>
                <span className="leader-metrics">{entry.totalPoints} pts • {entry.roundsWon} round wins • {entry.roundsPlayed} rounds</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="header-leaderboard-empty">No tournament rounds found for this period.</p>
        )}
      </section>

      <section className="stats-card">
        <h2>Past Tournament Winners</h2>
        <div className="leaderboard-actions-row" style={{ marginBottom: '0.55rem' }}>
          <button type="button" className="thin-header-btn" onClick={() => setHistoryPeriod('monthly')}>
            Monthly
          </button>
          <button type="button" className="thin-header-btn" onClick={() => setHistoryPeriod('weekly')}>
            Weekly
          </button>
        </div>
        {(tournamentHistory?.[historyPeriod] || []).length > 0 ? (
          <div className="admin-table-wrap">
            <table className="admin-table stats-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Tournament</th>
                  <th>Winner</th>
                  <th>Points</th>
                  <th>Round Wins</th>
                  <th>Rounds</th>
                  <th>Participants</th>
                </tr>
              </thead>
              <tbody>
                {(tournamentHistory?.[historyPeriod] || []).map((entry) => (
                  <tr key={`${historyPeriod}-${entry.periodLabel}`}>
                    <td>{entry.periodLabel}</td>
                    <td>{entry.tournamentName || 'Tournament'}</td>
                    <td>{entry.winner?.displayName || 'No winner recorded'}</td>
                    <td>{entry.winner?.totalPoints ?? 0}</td>
                    <td>{entry.winner?.roundsWon ?? 0}</td>
                    <td>{entry.winner?.roundsPlayed ?? 0}</td>
                    <td>{entry.participantsTotal ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="header-leaderboard-empty">No past tournament winner history for this period yet.</p>
        )}
      </section>

      <section className="stats-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem' }}>
          <h2>{showFullTopPlayers ? 'All Ranked Players By Total Points' : 'Top 20 Players By Total Points'}</h2>
          <button type="button" className="thin-header-btn" onClick={() => setShowFullTopPlayers((prev) => !prev)}>
            {showFullTopPlayers ? 'Show Top 20' : 'Expand Full List'}
          </button>
        </div>
        <p className="admin-note">Cumulative points across all games, sorted high to low.</p>
        <p className="admin-note">🤖 Terminator badge marks the player with the most bot defeats.</p>
        <div className="stats-grid-top10">
          {visibleTopPlayers.map((p, idx) => (
            <div key={p.userId} className="stats-pill">
              {renderActivityBadge(p)}
              <span className="leader-rank">#{idx + 1}</span>
              <span className="leader-name">
                {p.displayName}
                {terminatorUserIds.has(String(p.userId)) && (
                  <span className="terminator-badge" title="Terminator: most bots defeated" aria-label="Terminator badge">🤖</span>
                )}
              </span>
              <span className="leader-metrics">{p.totalPoints} pts • {p.gamesPlayed} games</span>
            </div>
          ))}
          {visibleTopPlayers.length === 0 && <p className="header-leaderboard-empty">No player stats yet.</p>}
        </div>
      </section>

      <section className="stats-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h2 style={{ marginBottom: 0 }}>All Players</h2>
            {user?.role === 'admin' && (
              <button
                type="button"
                className="thin-header-btn"
                title="Toggle audit columns"
                onClick={() => setShowAuditColumns((prev) => !prev)}
              >
                A
              </button>
            )}
          </div>
          <button type="button" className="thin-header-btn" onClick={() => setAllPlayersSectionOpen((prev) => !prev)}>
            {allPlayersSectionOpen ? 'Collapse' : 'Expand'}
          </button>
        </div>
        {allPlayersSectionOpen && (
          <>
            {user?.role === 'admin' && (
              <>
                <div className="leaderboard-actions-row" style={{ marginBottom: '0.55rem' }}>
                  <button type="button" className="thin-header-btn" onClick={runStatsAudit} disabled={statsAuditLoading}>
                    {statsAuditLoading ? 'Refreshing Audit...' : 'Refresh Audit'}
                  </button>
                </div>
                {statsAuditSummary && (
                  <p className="admin-note" style={{ marginBottom: '0.6rem' }}>
                    Audit scanned {statsAuditSummary.roundCount ?? 0} rounds across {statsAuditSummary.gameCount ?? 0} games.
                    {' '}Mismatched users: {statsAuditSummary.mismatchedUsers ?? 0}.
                    {' '}NPC history entries: {statsAuditSummary.npcHistoryEntries ?? 0}.
                    {' '}Orphaned entries: {statsAuditSummary.orphanedEntries ?? 0}.
                  </p>
                )}
                {statsAuditNotice && <p className="admin-note" style={{ marginBottom: '0.6rem' }}>{statsAuditNotice}</p>}
                {statsAuditError && <p className="error">{statsAuditError}</p>}
              </>
            )}
            <div className="stats-controls-row">
              <input
                type="text"
                value={playerQuery}
                onChange={(e) => setPlayerQuery(e.target.value)}
                placeholder="Filter players by name, username, or role"
              />
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table stats-table">
                <thead>
                  <tr>
                    <th><button type="button" className="table-sort-btn" onClick={() => togglePlayerSort('rank')}>Rank</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => togglePlayerSort('displayName')}>Player</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => togglePlayerSort('totalPoints')}>Points</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => togglePlayerSort('gamesPlayed')}>Games</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => togglePlayerSort('gamesWon')}>Wins</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => togglePlayerSort('roundsWon')}>Rounds Won</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => togglePlayerSort('courtesyShuffles')}>Courtesy</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => togglePlayerSort('humanOpponentsFaced')}>Faced Players</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => togglePlayerSort('npcOpponentsFaced')}>Faced NPCs</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => togglePlayerSort('humanOpponentsDefeated')}>Defeated Players</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => togglePlayerSort('npcOpponentsDefeated')}>Defeated NPCs</button></th>
                    {user?.role === 'admin' && showAuditColumns && <th>Audit Points (Stored/Expected/Delta)</th>}
                    {user?.role === 'admin' && showAuditColumns && <th>Audit Games (Stored/Expected/Delta)</th>}
                    {user?.role === 'admin' && showAuditColumns && <th>Audit W/R/C (Stored/Expected/Delta)</th>}
                    {user?.role === 'admin' && showAuditColumns && <th>Fix</th>}
                    {user?.role === 'admin' && showAuditColumns && <th>Games</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredPlayersWithAudit.map((p) => (
                    <tr key={p.userId}>
                      <td>{p.rank}</td>
                      <td>
                        {p.displayName}
                        {terminatorUserIds.has(String(p.userId)) && (
                          <span className="terminator-badge" title="Terminator: most bots defeated" aria-label="Terminator badge">🤖</span>
                        )}
                      </td>
                      <td>{p.totalPoints}</td>
                      <td>{p.gamesPlayed}</td>
                      <td>{p.gamesWon}</td>
                      <td>{p.roundsWon}</td>
                      <td>{p.courtesyShuffles}</td>
                      <td>{p.humanOpponentsFaced}</td>
                      <td>{p.npcOpponentsFaced}</td>
                      <td>{p.humanOpponentsDefeated}</td>
                      <td>{p.npcOpponentsDefeated}</td>
                      {user?.role === 'admin' && showAuditColumns && (
                        <td>
                          {p.auditStoredPoints == null
                            ? 'N/A'
                            : `${p.auditStoredPoints} / ${p.auditExpectedPoints} / ${p.auditDeltaPoints}`}
                          {p.auditHasMismatch && (
                            <strong style={{ marginLeft: '0.35rem' }}>!</strong>
                          )}
                        </td>
                      )}
                      {user?.role === 'admin' && showAuditColumns && (
                        <td>
                          {p.auditStoredGames == null
                            ? 'N/A'
                            : `${p.auditStoredGames} / ${p.auditExpectedGames} / ${p.auditDeltaGames}`}
                        </td>
                      )}
                      {user?.role === 'admin' && showAuditColumns && (
                        <td>
                          {p.auditStoredWins == null
                            ? 'N/A'
                            : `W ${p.auditStoredWins}/${p.auditExpectedWins}/${p.auditDeltaWins} • R ${p.auditStoredRoundsWon}/${p.auditExpectedRoundsWon}/${p.auditDeltaRoundsWon} • C ${p.auditStoredCourtesy}/${p.auditExpectedCourtesy}/${p.auditDeltaCourtesy}`}
                        </td>
                      )}
                      {user?.role === 'admin' && showAuditColumns && (
                        <td>
                          <button
                            type="button"
                            className="thin-header-btn"
                            onClick={() => handleReconcileUser(p)}
                            disabled={reconcilingUserId === p.userId || !p.auditHasMismatch}
                            title={p.auditHasMismatch ? 'Reconcile this player from round history' : 'No mismatch detected'}
                          >
                            {reconcilingUserId === p.userId ? 'Fixing...' : 'Fix'}
                          </button>
                        </td>
                      )}
                      {user?.role === 'admin' && showAuditColumns && (
                        <td>
                          <button
                            type="button"
                            className="thin-header-btn"
                            onClick={() => openPlayerBreakdown(p)}
                            disabled={breakdownLoadingUserId === p.userId}
                          >
                            {breakdownLoadingUserId === p.userId ? 'Loading...' : 'View'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {filteredPlayersWithAudit.length === 0 && (
                    <tr>
                      <td colSpan={user?.role === 'admin' && showAuditColumns ? 16 : 11}>No players match this filter.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <section className="stats-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem' }}>
          <h2>All Games</h2>
          <button type="button" className="thin-header-btn" onClick={() => setAllGamesSectionOpen((prev) => !prev)}>
            {allGamesSectionOpen ? 'Collapse' : 'Expand'}
          </button>
        </div>
        {allGamesSectionOpen && (
          <>
            <div className="stats-controls-row">
              <input
                type="text"
                value={gameQuery}
                onChange={(e) => setGameQuery(e.target.value)}
                placeholder="Filter games by room code, name, or status"
              />
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table stats-table">
                <thead>
                  <tr>
                    <th><button type="button" className="table-sort-btn" onClick={() => toggleGameSort('roomCode')}>Room</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => toggleGameSort('gameName')}>Game</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => toggleGameSort('status')}>Status</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => toggleGameSort('humanPlayers')}>Humans</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => toggleGameSort('botPlayers')}>NPCs</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => toggleGameSort('tournamentRecurrence')}>Tournament</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => toggleGameSort('roundsPlayed')}>Rounds</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => toggleGameSort('sessionPointsTotal')}>Session Points</button></th>
                    <th><button type="button" className="table-sort-btn" onClick={() => toggleGameSort('createdAt')}>Created</button></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGames.map((g) => (
                    <tr key={g.gameId}>
                      <td>{g.roomCode}</td>
                      <td>{g.gameName}</td>
                      <td>{g.status}{g.isPersistent ? ' • Persistent' : ''}{g.isPrivate ? ' • Private' : ''}</td>
                      <td>{g.humanPlayers}</td>
                      <td>{g.botPlayers}</td>
                      <td>{g.tournamentEnabled ? `${g.tournamentRecurrence}${g.tournamentLabel ? ` • ${g.tournamentLabel}` : ''}` : 'No'}</td>
                      <td>{g.roundsPlayed}</td>
                      <td>{g.sessionPointsTotal}</td>
                      <td>{new Date(g.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                  {filteredGames.length === 0 && (
                    <tr>
                      <td colSpan={9}>No games match this filter.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {breakdownModal.open && (
        <div className="swap-modal-overlay" role="dialog" aria-modal="true" aria-label="Player points breakdown modal">
          <div className="swap-modal-panel" style={{ maxWidth: '980px', width: '95%' }}>
            <h3 className="swap-modal-title">Points Breakdown - {breakdownModal.playerName}</h3>
            {breakdownModal.error && <p className="error">{breakdownModal.error}</p>}
            {!breakdownModal.error && breakdownModal.summary && (
              <>
                <p className="admin-note" style={{ marginBottom: '0.35rem' }}>
                  Rounds: {breakdownModal.summary.rounds ?? 0} • Round recorded points: {breakdownModal.summary.recordedPoints ?? 0} • Round expected points: {breakdownModal.summary.expectedPoints ?? 0} • Round delta: {breakdownModal.summary.delta ?? 0} • Mismatch rounds: {breakdownModal.summary.mismatchRounds ?? 0}
                </p>
                <p className="admin-note" style={{ marginBottom: '0.6rem' }}>
                  Lifetime stored points: {breakdownModal.summary.stored?.totalPoints ?? 0} • Unattributed drift (stored vs round-recorded): {breakdownModal.summary.unattributed?.pointsVsRecordedHistory ?? 0} • Unattributed drift (stored vs round-expected): {breakdownModal.summary.unattributed?.pointsVsExpectedHistory ?? 0}
                </p>
              </>
            )}
            {!breakdownModal.error && (
              <>
                <div className="leaderboard-actions-row" style={{ marginBottom: '0.55rem' }}>
                  <button
                    type="button"
                    className="thin-header-btn"
                    onClick={() => setBreakdownShowNonZeroOnly((prev) => !prev)}
                  >
                    {breakdownShowNonZeroOnly ? 'Show All Rounds' : 'Show Delta != 0 Only'}
                  </button>
                </div>
                <div className="admin-table-wrap">
                <table className="admin-table stats-table">
                  <thead>
                    <tr>
                      <th>Room</th>
                      <th>Game</th>
                      <th>Round</th>
                      <th>Position</th>
                      <th>Players</th>
                      <th>NPCs</th>
                      <th>Humans</th>
                      <th>Recorded</th>
                      <th>Expected</th>
                      <th>Delta</th>
                      <th>Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedBreakdownRounds.map((round) => (
                      <tr
                        key={`${round.gameId}-${round.roundNumber}-${round.completedAt}`}
                        style={Number(round.delta || 0) !== 0 ? { background: 'rgba(255, 80, 80, 0.10)' } : undefined}
                      >
                        <td>{round.roomCode}</td>
                        <td>{round.gameName}</td>
                        <td>{round.roundNumber ?? '-'}</td>
                        <td>{round.position || '-'}</td>
                        <td>{round.totalPlayers}</td>
                        <td>{round.npcCount}</td>
                        <td>{round.humanCount}</td>
                        <td>{round.recordedPoints}</td>
                        <td>{round.expectedPoints}</td>
                        <td><strong>{round.delta}</strong></td>
                        <td>{round.completedAt ? new Date(round.completedAt).toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                    {displayedBreakdownRounds.length === 0 && (
                      <tr>
                        <td colSpan={11}>{breakdownShowNonZeroOnly ? 'No non-zero delta rounds found for this player.' : 'No rounds found for this player.'}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.85rem' }}>
              <button
                type="button"
                className="btn-play"
                onClick={() => {
                  setBreakdownShowNonZeroOnly(false);
                  setBreakdownModal({ open: false, playerName: '', summary: null, rounds: [], error: '' });
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsPage;
